<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { BellOff, CheckCheck, Trash2 } from 'lucide-vue-next'
import { computed, onMounted, onUnmounted } from 'vue'

import RelativeTime from '@/components/RelativeTime.vue'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { NOTIFICATION_POLL_INTERVAL } from '@/lib/constants'
import type { Notification } from '@/types'

const props = defineProps<{
  notifications: Notification[]
  readPath: (id: number) => string
  readAllPath: string
  deletePath: (id: number) => string
  deleteAllPath: string
}>()

let timer: ReturnType<typeof setInterval> | null = null
onMounted(() => { timer = setInterval(() => router.reload({ only: ['notifications'] }), NOTIFICATION_POLL_INTERVAL) })
onUnmounted(() => { if (timer) clearInterval(timer) })

const unread = computed(() => props.notifications.filter(n => !n.read_at))

function markRead(n: Notification) {
  if (!n.read_at) {
    router.patch(props.readPath(n.id), {}, { preserveScroll: true })
  }
  if (n.action_url) window.location.href = n.action_url
}

function markAllRead() {
  router.patch(props.readAllPath, {}, { preserveScroll: true })
}

function deleteNotification(n: Notification) {
  router.delete(props.deletePath(n.id), { preserveScroll: true })
}

function deleteAll() {
  router.delete(props.deleteAllPath, { preserveScroll: true })
}

</script>

<template>
  <div class="mx-auto w-full max-w-3xl pt-6">
    <div class="mb-6 flex items-center justify-between gap-3">
      <div class="flex min-w-0 items-center gap-3">
        <h1 class="text-xl font-semibold">Notifications</h1>
        <span
          v-if="unread.length > 0"
          class="bg-primary text-primary-foreground shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold"
        >
          {{ unread.length }} unread
        </span>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <Button v-if="unread.length > 0" variant="outline" size="sm" @click="markAllRead">
          <CheckCheck class="size-4 sm:mr-2" />
          <span class="hidden sm:inline">Mark all read</span>
        </Button>
        <Button v-if="notifications.length > 0" variant="outline" size="sm" @click="deleteAll">
          <Trash2 class="size-4 sm:mr-2" />
          <span class="hidden sm:inline">Delete all</span>
        </Button>
      </div>
    </div>

    <Card class="overflow-hidden">
      <div v-if="notifications.length === 0" class="text-muted-foreground flex flex-col items-center gap-3 py-16">
        <BellOff class="size-10 opacity-30" />
        <p class="text-sm">You're all caught up</p>
      </div>

      <ul v-else class="divide-y">
        <li
          v-for="n in notifications"
          :key="n.id"
          class="hover:bg-muted/40 flex cursor-pointer items-start gap-4 px-5 py-4 transition-colors"
          :class="{ 'bg-primary/5': !n.read_at }"
          @click="markRead(n)"
        >
          <div class="mt-1.5 shrink-0">
            <span class="block size-2 rounded-full" :class="n.read_at ? 'bg-transparent' : 'bg-primary'" />
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm leading-snug" :class="n.read_at ? 'text-muted-foreground' : 'font-semibold'">
              {{ n.title }}
            </p>
            <p v-if="n.body" class="text-muted-foreground mt-1 text-xs leading-relaxed">{{ n.body }}</p>
            <RelativeTime :date="n.created_at" class="text-muted-foreground mt-1.5 block text-xs" />
          </div>
          <button
            class="text-muted-foreground hover:text-destructive mt-1 shrink-0 transition-colors"
            @click.stop="deleteNotification(n)"
          >
            <Trash2 class="size-4" />
          </button>
        </li>
      </ul>
    </Card>
  </div>
</template>
